import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { MembersEmptyPage } from "@components/common/empty-page/members-empty-page";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { MembersSort } from "@components/table/members/members-sort";
import { MembersTable } from "@components/table/members/members-table";
import { Box } from "@mui/material";
import { useCompanyId } from "@context-providers/company-id-provider";
import {
  activeMembersSelector,
  fetchingActiveMembersFlagsSelector,
} from "@store/active-members/active-members-selector";
import { useAppSelector } from "@store/store-helper";
import { useMembersUtils } from "@hooks/use-member-utils";
import { getSvgColoredIconCss, HIDE_ON_HOVER_CLASS } from "@utils/ui-utils";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { WORKSPACE_MEMBERS_TABLE_COLUMNS } from "@components/table/members/members-table-constants";
import { MembersSortBy } from "@custom-types/member-types";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { isMembersSortBy } from "@custom-types/type-guards";
import { DEFAULT_WORKSPACE_MEMBERS_SORT_BY } from "@components/table/members/members-sort-constants";
import { red, XMarkCircleFillIcon } from "@flat-ui";

/** Listing all the active members counted in current plan in a table */
export function ActiveMembersTable(): JSX.Element {
  const companyId = useCompanyId();
  const activeMembers = useAppSelector(activeMembersSelector);
  const { status } = useAppSelector(fetchingActiveMembersFlagsSelector);
  const { openProfilePage } = useMembersUtils();
  const { sortBy, setSortBy } = useSubjectsSortByQuery<MembersSortBy>({
    defaultSort: DEFAULT_WORKSPACE_MEMBERS_SORT_BY,
    isValidSort: isMembersSortBy,
  });

  const isLoading = status === "pending" || status === "uninitialized";

  // Show failed screen if request for fetching active members is failed
  if (status === "rejected") {
    return (
      <EmptyPage
        title="Error"
        subtitle={
          "Failed to fetch the active members. Please reload the page and try again."
        }
        icon={() => (
          <XMarkCircleFillIcon
            sx={{
              ...getSvgColoredIconCss(red[500]),
              width: "61px",
              height: "61px",
            }}
          />
        )}
      />
    );
  }

  // Show empty screen if request for fetching active members succeeded but there is no active member
  if (status === "succeeded" && activeMembers.length === 0) {
    return (
      <MembersEmptyPage
        subtitle="All of your active members will be found here that are counted in the current plan."
        button={null}
      />
    );
  }

  return (
    <Box
      sx={{
        marginTop: SPACE_ELEMENTS_OF_MODAL,
        overflow: "hidden",
      }}
    >
      <EntitiesToolbar
        toolbarEntityName="Member"
        numberOfRows={activeMembers.length}
        isLoading={isLoading}
        sortActions={
          activeMembers.length > 0 && (
            <MembersSort
              sortBy={sortBy}
              setSortBy={setSortBy}
              tableSubject="activeMembers"
            />
          )
        }
      />
      <ColumnPreferencesProvider
        tableKey="active-members-table"
        columns={WORKSPACE_MEMBERS_TABLE_COLUMNS}
        options={{
          nonConfigurableColumns: ["options"],
          defaultVisibleColumns: ["user", "email", "status", "role"],
        }}
      >
        <MembersTable
          companyId={companyId}
          members={activeMembers}
          tableSubject="activeMembers"
          shouldHideCheckbox={true}
          isLoading={isLoading}
          sortBy={sortBy}
          setSortBy={setSortBy}
          onRowClick={(member) => {
            openProfilePage({
              member,
              source: "active members table",
            });
          }}
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },

            [`& .MuiDataGrid-row:hover .${HIDE_ON_HOVER_CLASS}`]: {
              display: "none",
            },
            "& .MuiDataGrid-columnHeader:hover .drag-handle": {
              display: "flex",
            },

            /** Fill all the header */
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              width: "100%",
            },
          }}
        />
      </ColumnPreferencesProvider>
    </Box>
  );
}
